import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { API } from '../utils/api';
import LucideIcon from './LucideIcon';
import WhiskeyLogo from './layout/WhiskeyLogo';
import XPToast from './gamification/XPToast';

/**
 * Spaced review: a recognition question about a technique the learner has
 * already completed a lesson on, resurfaced on a schedule.
 *
 * See docs/build-plan/08-lesson-design.md finding 3 -- this is the mechanic
 * that makes the streak mean something: not "opened the app today" but
 * "successfully retrieved a technique learned days ago." Grading is entirely
 * server-side (backend/src/index.js's handleReviewSubmit); this component
 * never has the answer key until the server sends it back after an answer.
 *
 * GET /review always returns the single most-overdue technique still due.
 * Answering it moves that technique's due date out, so calling GET again
 * naturally serves the next most-overdue one -- which is what lets this
 * component "clear the queue" one item at a time without the backend needing
 * a batch endpoint.
 */

interface PromptOption { id: string; prompt: string; correct?: boolean; whyWrong?: string | null; }
interface ReasonOption { id: string; text: string; correct?: boolean; }
interface ReviewItem { id: string; setup: string; options: PromptOption[]; reasonOptions: ReasonOption[]; }

interface DueSession {
  due: true;
  dueCount: number;
  lessonId: string;
  technique: string;
  overdueBy: number;
  item: ReviewItem;
}
interface NoneDue {
  due: false;
  dueCount: 0;
  nextDueDate: string | null;
}

interface SubmitResult {
  correct: boolean;
  promptCorrect: boolean;
  reasonCorrect: boolean;
  points: number;
  item: ReviewItem;
}

/**
 * A 200 that carries no grading at all -- the backend's response when this
 * exact review was already credited today (another tab, or a client retry
 * landing after an earlier request's write actually succeeded server-side).
 * A real, reachable shape, not a hypothetical: reviewSubmitDecision() in
 * schedule.js returns this deliberately rather than re-grading.
 */
interface DuplicateResult { success: true; duplicate: true; message?: string; }

function isDuplicateResult(data: unknown): data is DuplicateResult {
  return !!data && typeof data === 'object' && (data as any).duplicate === true;
}

const panel: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 22,
};

function daysUntil(dateStr: string): number {
  const target = new Date(`${dateStr}T00:00:00Z`).getTime();
  const today = new Date(`${new Date().toISOString().split('T')[0]}T00:00:00Z`).getTime();
  return Math.round((target - today) / 86400000);
}

const Review: React.FC = () => {
  const [session, setSession] = useState<DueSession | NoneDue | null>(null);
  const [error, setError] = useState('');
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<SubmitResult | null>(null);
  const [clearedCount, setClearedCount] = useState(0);
  const [xpBurst, setXpBurst] = useState<number | null>(null);

  const loadNext = useCallback(async () => {
    setError('');
    setResult(null);
    setSelectedOption(null);
    setSelectedReason(null);
    try {
      const data = await API.get('/review');
      setSession(data);
    } catch (e: any) {
      setError(e?.message || 'Could not load your review.');
    }
  }, []);

  useEffect(() => {
    loadNext();
  }, [loadNext]);

  const submit = useCallback(async () => {
    if (!session || !session.due || !selectedOption || !selectedReason || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const data: SubmitResult | DuplicateResult = await API.post('/review', {
        lessonId: session.lessonId,
        optionId: selectedOption,
        reasonOptionId: selectedReason,
      });
      if (isDuplicateResult(data)) {
        // Already credited elsewhere -- there is nothing to grade or reveal.
        // Move straight to whatever's next rather than render a result shape
        // that was never sent, or silently do nothing and leave the button
        // looking like it hung.
        await loadNext();
        return;
      }
      setResult(data);
      if (data.points > 0) setXpBurst(data.points);
      window.dispatchEvent(new CustomEvent('challengeCompleted', {
        detail: { challengeId: `review-${session.lessonId}`, topic: 'basic-prompt-engineering', points: data.points },
      }));
    } catch (e: any) {
      setError(e?.message || 'Could not grade your answer. Try again.');
    } finally {
      setSubmitting(false);
    }
  }, [session, selectedOption, selectedReason, submitting, loadNext]);

  const continueSession = useCallback(() => {
    setClearedCount((c) => c + 1);
    setXpBurst(null);
    loadNext();
  }, [loadNext]);

  if (error && !session) {
    return (
      <div style={{ ...panel, maxWidth: 560, margin: '0 auto' }}>
        <h1 className="ws-display" style={{ margin: '0 0 8px', fontSize: 20 }}>Couldn&rsquo;t load your review</h1>
        <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{error}</p>
        <button type="button" onClick={loadNext} className="btn-live" style={{ background: 'var(--ink)', color: 'white', fontWeight: 700, fontSize: 14, padding: '10px 20px', borderRadius: 10, border: 'none', cursor: 'pointer' }}>
          Try again
        </button>
      </div>
    );
  }

  if (!session) {
    return <p style={{ fontSize: 14, color: 'var(--text-tertiary)' }}>Loading your review&hellip;</p>;
  }

  // ---- Nothing due -----------------------------------------------------
  if (!session.due) {
    const days = session.nextDueDate ? daysUntil(session.nextDueDate) : null;
    return (
      <div className="ws-pop-in" style={{ ...panel, maxWidth: 560, margin: '0 auto', textAlign: 'center', padding: '44px 30px' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}><WhiskeyLogo size={64} /></div>
        {clearedCount > 0 && (
          <p className="ws-mono" style={{ margin: '0 0 4px', fontSize: 12, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ember-strong)' }}>
            {clearedCount} {clearedCount === 1 ? 'review' : 'reviews'} cleared
          </p>
        )}
        <h1 className="ws-display" style={{ margin: '0 0 10px', fontSize: 24, fontWeight: 700 }}>
          {clearedCount > 0 ? 'Queue cleared' : 'Nothing due right now'}
        </h1>
        <p style={{ margin: '0 auto 22px', fontSize: 14.5, color: 'var(--text-secondary)', lineHeight: 1.6, maxWidth: 400 }}>
          {days !== null && days > 0
            ? `Your next review unlocks in ${days} day${days === 1 ? '' : 's'}. Spacing it out is what makes it work — coming back too soon would just be re-reading.`
            : session.nextDueDate
              ? 'Your next review unlocks today — check back in a bit.'
              : 'Finish a lesson and its first review will show up here the next day.'}
        </p>
        <Link to="/" className="btn-live" style={{ display: 'inline-block', background: 'var(--ink)', color: 'white', fontWeight: 700, fontSize: 14, padding: '10px 20px', borderRadius: 10, textDecoration: 'none' }}>
          Back to dashboard
        </Link>
      </div>
    );
  }

  const { item, technique, dueCount, overdueBy } = session;

  // ---- Answered: reveal ---------------------------------------------------
  if (result && (!result.item || !Array.isArray(result.item.options) || !Array.isArray(result.item.reasonOptions))) {
    // Not a duplicate (filtered above) but still missing a full item. The
    // frontend (Netlify/CloudFront) and backend (Lambda) deploy independently
    // here -- see CLAUDE.md's hosting section -- so a client can genuinely run
    // briefly against an API response shape it predates. Never render into a
    // shape this build doesn't understand.
    return (
      <div className="ws-pop-in" style={{ ...panel, maxWidth: 560, margin: '0 auto', textAlign: 'center', padding: '36px 26px' }}>
        <h1 className="ws-display" style={{ margin: '0 0 8px', fontSize: 20 }}>Recorded, but couldn&rsquo;t show the details</h1>
        <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          Your answer was submitted. Come back and it&rsquo;ll pick up from here.
        </p>
        <Link to="/" className="btn-live" style={{ display: 'inline-block', background: 'var(--ink)', color: 'white', fontWeight: 700, fontSize: 14, padding: '10px 20px', borderRadius: 10, textDecoration: 'none' }}>
          Back to dashboard
        </Link>
      </div>
    );
  }

  if (result) {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div className="ws-pop-in" style={{ ...panel, textAlign: 'center', padding: '30px 26px' }}>
          <h1 className="ws-display" style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 700 }}>
            {result.correct ? 'Recalled it' : 'Worth another look'}
          </h1>
          <p style={{ margin: '0 0 14px', fontSize: 13.5, color: 'var(--text-secondary)' }}>
            {result.correct
              ? `You picked the right prompt and knew why it works, for ${technique}.`
              : !result.promptCorrect
                ? `The right prompt for ${technique} looked different from the one you picked — see why below.`
                : `You picked the right prompt, but not quite the right reason — see why below.`}
          </p>
          {xpBurst !== null && xpBurst > 0 && <XPToast points={xpBurst} animate={false} />}
        </div>

        <div style={panel}>
          <p className="ws-mono" style={{ margin: '0 0 10px', fontSize: 10.5, fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
            The prompt
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {result.item.options.map((o) => (
              <div key={o.id} style={{
                border: `1.5px solid ${o.correct ? 'var(--success)' : o.id === selectedOption ? 'var(--danger, #b42318)' : 'var(--border)'}`,
                borderRadius: 10, padding: '12px 14px', background: o.correct ? 'oklch(0.75 0.14 155 / 0.08)' : 'var(--surface-sunken)',
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <span style={{ flex: '0 0 auto', marginTop: 1, color: o.correct ? 'var(--success)' : 'var(--text-tertiary)' }}>
                    <LucideIcon name={o.correct ? 'CircleCheck' : 'Circle'} size={15} />
                  </span>
                  <pre className="ws-mono" style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, lineHeight: 1.7, color: 'var(--text-primary)' }}>{o.prompt}</pre>
                </div>
                {!o.correct && o.whyWrong && (
                  <p style={{ margin: '8px 0 0', paddingLeft: 23, fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-secondary)' }}>{o.whyWrong}</p>
                )}
              </div>
            ))}
          </div>

          <p className="ws-mono" style={{ margin: '18px 0 10px', fontSize: 10.5, fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
            Why it works
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {result.item.reasonOptions.map((r) => (
              <div key={r.id} style={{
                display: 'flex', alignItems: 'flex-start', gap: 8,
                border: `1.5px solid ${r.correct ? 'var(--success)' : 'var(--border)'}`,
                borderRadius: 10, padding: '10px 14px', background: r.correct ? 'oklch(0.75 0.14 155 / 0.08)' : 'var(--surface-sunken)',
              }}>
                <span style={{ flex: '0 0 auto', marginTop: 1, color: r.correct ? 'var(--success)' : 'var(--text-tertiary)' }}>
                  <LucideIcon name={r.correct ? 'CircleCheck' : 'Circle'} size={15} />
                </span>
                <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: 'var(--text-primary)' }}>{r.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button type="button" onClick={continueSession} className="btn-live ws-display"
            style={{ padding: '12px 28px', borderRadius: 10, border: 'none', fontSize: 14.5, fontWeight: 700, cursor: 'pointer', background: 'var(--ember)', color: 'white' }}>
            {dueCount > 1 ? 'Next review' : 'Done for today'}
          </button>
        </div>
      </div>
    );
  }

  // ---- Question -------------------------------------------------------
  return (
    <div style={{ maxWidth: 720, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <span className="ws-display" style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ember-strong)' }}>
          Review &middot; {technique}
        </span>
        <span className="ws-mono" style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
          {dueCount} due &middot; last practised {overdueBy <= 0 ? 'on schedule' : `${overdueBy} day${overdueBy === 1 ? '' : 's'} ago`}
        </span>
      </div>

      <div className="ws-fade-up" style={{ ...panel, background: 'var(--surface-sunken)', borderStyle: 'dashed' }}>
        <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--text-primary)' }}>{item.setup}</p>
      </div>

      <div className="ws-fade-up" style={panel}>
        <p className="ws-mono" style={{ margin: '0 0 10px', fontSize: 10.5, fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
          Which prompt gets this right?
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {item.options.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => setSelectedOption(o.id)}
              aria-pressed={selectedOption === o.id}
              style={{
                textAlign: 'left', cursor: 'pointer', borderRadius: 10, padding: '12px 14px',
                border: `1.5px solid ${selectedOption === o.id ? 'var(--ember)' : 'var(--border)'}`,
                background: selectedOption === o.id ? 'var(--ember-soft)' : 'var(--surface)',
              }}
            >
              <pre className="ws-mono" style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, lineHeight: 1.7, color: 'var(--text-primary)' }}>{o.prompt}</pre>
            </button>
          ))}
        </div>

        <p className="ws-mono" style={{ margin: '20px 0 10px', fontSize: 10.5, fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
          And why?
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {item.reasonOptions.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setSelectedReason(r.id)}
              aria-pressed={selectedReason === r.id}
              style={{
                textAlign: 'left', cursor: 'pointer', borderRadius: 10, padding: '10px 14px',
                border: `1.5px solid ${selectedReason === r.id ? 'var(--ember)' : 'var(--border)'}`,
                background: selectedReason === r.id ? 'var(--ember-soft)' : 'var(--surface)',
              }}
            >
              <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: 'var(--text-primary)' }}>{r.text}</p>
            </button>
          ))}
        </div>

        {error && <p role="alert" className="ws-rise" style={{ margin: '14px 0 0', fontSize: 13.5, color: 'var(--danger, #b42318)' }}>{error}</p>}

        <div style={{ marginTop: 18 }}>
          <button
            type="button"
            onClick={submit}
            disabled={!selectedOption || !selectedReason || submitting}
            className="btn-live ws-display"
            style={{
              padding: '11px 24px', borderRadius: 10, border: 'none', fontSize: 14.5, fontWeight: 700,
              cursor: !selectedOption || !selectedReason || submitting ? 'not-allowed' : 'pointer',
              background: !selectedOption || !selectedReason || submitting ? 'var(--surface-sunken)' : 'var(--ember)',
              color: !selectedOption || !selectedReason || submitting ? 'var(--text-tertiary)' : 'white',
            }}
          >
            {submitting ? 'Checking…' : 'Check my answer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Review;
